
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { DollarSign, Edit, Check, X } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Cliente, Gerador } from "@/types";
import TarifaActions from "./TarifaActions";

interface ClientesTableProps {
  clientes: Cliente[];
  gerador: Gerador;
  editingTarifas: Record<string, number>;
  originalTarifas: Record<string, number>;
  editingTarifasGerador: Record<string, number>;
  originalTarifasGerador: Record<string, number>;
  onTarifaChange: (clienteId: string, value: string) => void;
  onSaveTarifa: (clienteId: string) => void;
  onUndoTarifa: (clienteId: string) => void;
  onTarifaGeradorChange: (clienteId: string, value: string) => void;
  onSaveTarifaGerador: (clienteId: string) => void;
  onUndoTarifaGerador: (clienteId: string) => void;
}

const ClientesTable = ({
  clientes,
  gerador,
  editingTarifas,
  originalTarifas,
  editingTarifasGerador,
  originalTarifasGerador,
  onTarifaChange,
  onSaveTarifa,
  onUndoTarifa,
  onTarifaGeradorChange,
  onSaveTarifaGerador,
  onUndoTarifaGerador
}: ClientesTableProps) => {
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader className="bg-gray-50">
          <TableRow className="border-b border-gray-200">
            <TableHead className="w-[200px] py-3 text-gray-600 font-medium">Cliente</TableHead>
            <TableHead className="py-3 text-gray-600 font-medium">Instalação</TableHead>
            <TableHead className="text-right py-3 text-gray-600 font-medium">Gasto Mensal</TableHead>
            <TableHead className="text-right py-3 text-gray-600 font-medium">Tarifa Cliente</TableHead>
            <TableHead className="text-right py-3 text-gray-600 font-medium">Tarifa Gerador</TableHead>
            <TableHead className="text-right py-3 text-gray-600 font-medium">Consumo</TableHead>
            <TableHead className="text-right py-3 text-gray-600 font-medium">Economia</TableHead>
            <TableHead className="text-right py-3 text-gray-600 font-medium">Novo Valor</TableHead>
            <TableHead className="text-right py-3 text-gray-600 font-medium">% do Gerador</TableHead>
            <TableHead className="text-right w-[100px] py-3 text-gray-600 font-medium">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {clientes.length === 0 ? (
            <TableRow>
              <TableCell colSpan={10} className="text-center h-24 text-gray-500">
                Nenhum cliente encontrado para este gerador
              </TableCell>
            </TableRow>
          ) : (
            clientes.map((cliente) => {
              // Get the client-specific generator rate or use the default
              const tarifaGerador = cliente.tarifaGerador !== undefined 
                ? cliente.tarifaGerador 
                : gerador.tarifaVenda;
              
              return (
                <TableRow key={cliente.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <TableCell className="font-medium py-4">
                    <div className="font-medium text-gray-800">{cliente.nome}</div>
                    <div className="text-xs text-gray-500">{cliente.cidade}/{cliente.estado}</div>
                  </TableCell>
                  <TableCell className="py-4">
                    <div className="text-sm text-gray-700">Nº Cliente: {cliente.numeroCliente}</div>
                    <div className="text-xs text-gray-500">Nº Inst.: {cliente.numeroInstalacao}</div>
                  </TableCell>
                  <TableCell className="text-right font-medium text-gray-800 py-4">
                    R$ {cliente.gastoEnergia.toFixed(2)}
                  </TableCell>
                  <TableCell className="text-right py-4">
                    <TarifaActions 
                      clienteId={cliente.id}
                      editingTarifa={editingTarifas[cliente.id] || cliente.tarifaEnergia}
                      originalTarifa={originalTarifas[cliente.id] || cliente.tarifaEnergia}
                      onTarifaChange={onTarifaChange}
                      onSaveTarifa={onSaveTarifa}
                      onUndoTarifa={onUndoTarifa}
                    />
                  </TableCell>
                  <TableCell className="text-right py-4">
                    <TarifaActions 
                      clienteId={cliente.id}
                      editingTarifa={editingTarifasGerador[cliente.id] || tarifaGerador}
                      originalTarifa={originalTarifasGerador[cliente.id] || tarifaGerador}
                      onTarifaChange={onTarifaGeradorChange}
                      onSaveTarifa={onSaveTarifaGerador}
                      onUndoTarifa={onUndoTarifaGerador}
                    />
                  </TableCell>
                  <TableCell className="text-right py-4 text-gray-700">
                    {cliente.consumoMedio?.toFixed(2) || "0.00"} kWh
                  </TableCell>
                  <TableCell className="text-right py-4">
                    <div className="text-green-600 font-medium">
                      R$ {cliente.economiaEstimada?.toFixed(2) || "0.00"}
                    </div>
                    <div className="text-xs text-green-500">
                      {cliente.percentualEconomia?.toFixed(1) || "0.0"}%
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-medium text-blue-600 py-4">
                    R$ {(cliente.valorCalculado || (cliente.gastoEnergia * tarifaGerador)).toFixed(2)}
                  </TableCell>
                  <TableCell className="text-right py-4">
                    <div className="flex flex-col items-end">
                      <span className="text-gray-700">{cliente.percentualEnergiaAdquirida?.toFixed(1) || "0.0"}%</span>
                      <div className="w-16 mt-1">
                        <Progress 
                          value={cliente.percentualEnergiaAdquirida || 0} 
                          className="h-1.5 bg-gray-200" 
                        />
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-right py-4">
                    <Link to={`/clientes/${cliente.id}`}>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-gray-600 hover:text-blue-600 hover:bg-blue-50">
                        <DollarSign className="h-4 w-4" />
                      </Button>
                    </Link>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default ClientesTable;
